/**
 * Central environment-config access layer (ENG-3001).
 *
 * Every environment-dependent value must be read through `env` — never via
 * `import.meta.env` directly. Each key resolves in this order:
 *
 *   1. `window.__env` — runtime config loaded from `/config.js`, which the
 *      container entrypoint (nginx/40-runtime-config.sh) generates from
 *      container env vars at startup. This is what lets ONE image serve
 *      every environment (ENG3002 and future burstable envs).
 *   2. `import.meta.env` — Vite build-time values (.env / .env.<mode>),
 *      the fallback for local dev and legacy per-environment builds.
 *
 * Empty-string runtime values are treated as unset so a container that
 * emits nothing for a key falls back to the build-time value.
 */

const runtimeEnv = (key: keyof ImportMetaEnv): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const value = window.__env?.[key]
  return value === undefined || value === '' ? undefined : value
}

export const env = {
  // ── Per-environment values — override these via container env vars ──
  VITE_API_BASE_URL:
    runtimeEnv('VITE_API_BASE_URL') ?? import.meta.env.VITE_API_BASE_URL,
  VITE_MEETING_BASE_URL:
    runtimeEnv('VITE_MEETING_BASE_URL') ??
    import.meta.env.VITE_MEETING_BASE_URL,
  VITE_OLD_PARTICIPANT_PORTAL_BASE_URL:
    runtimeEnv('VITE_OLD_PARTICIPANT_PORTAL_BASE_URL') ??
    import.meta.env.VITE_OLD_PARTICIPANT_PORTAL_BASE_URL,
  VITE_CHECKOUT_CALLBACK_URL_SUCCESS:
    runtimeEnv('VITE_CHECKOUT_CALLBACK_URL_SUCCESS') ??
    import.meta.env.VITE_CHECKOUT_CALLBACK_URL_SUCCESS,
  VITE_LOGIN_COOKIE_DOMAIN:
    runtimeEnv('VITE_LOGIN_COOKIE_DOMAIN') ??
    import.meta.env.VITE_LOGIN_COOKIE_DOMAIN,
  VITE_ACCESS_TOKEN_COOKIE_NAME:
    runtimeEnv('VITE_ACCESS_TOKEN_COOKIE_NAME') ??
    import.meta.env.VITE_ACCESS_TOKEN_COOKIE_NAME,
  VITE_REFRESH_TOKEN_COOKIE_NAME:
    runtimeEnv('VITE_REFRESH_TOKEN_COOKIE_NAME') ??
    import.meta.env.VITE_REFRESH_TOKEN_COOKIE_NAME,
  VITE_GTM_KEY: runtimeEnv('VITE_GTM_KEY') ?? import.meta.env.VITE_GTM_KEY,
  VITE_USER_PILOT_KEY:
    runtimeEnv('VITE_USER_PILOT_KEY') ?? import.meta.env.VITE_USER_PILOT_KEY,
  VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID:
    runtimeEnv('VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID') ??
    import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID,
  VITE_STRIPE_KEY:
    runtimeEnv('VITE_STRIPE_KEY') ?? import.meta.env.VITE_STRIPE_KEY,
  VITE_STRIPE_SUBSCRIPTION_KEY:
    runtimeEnv('VITE_STRIPE_SUBSCRIPTION_KEY') ??
    import.meta.env.VITE_STRIPE_SUBSCRIPTION_KEY,
  VITE_PRIOR_MINUTES_TO_JOIN_THE_EVENT:
    runtimeEnv('VITE_PRIOR_MINUTES_TO_JOIN_THE_EVENT') ??
    import.meta.env.VITE_PRIOR_MINUTES_TO_JOIN_THE_EVENT,
  VITE_ENABLE_DEVTOOLS:
    runtimeEnv('VITE_ENABLE_DEVTOOLS') ?? import.meta.env.VITE_ENABLE_DEVTOOLS,
  VITE_GOOGLE_MAP_API_KEY:
    runtimeEnv('VITE_GOOGLE_MAP_API_KEY') ??
    import.meta.env.VITE_GOOGLE_MAP_API_KEY,

  // ── Environment-agnostic values (base .env) — same for every env ──
  VITE_WALLET_WIDGET_STATE_KEY:
    runtimeEnv('VITE_WALLET_WIDGET_STATE_KEY') ??
    import.meta.env.VITE_WALLET_WIDGET_STATE_KEY,
  VITE_TEMPLATES_BANNER_STATE_KEY:
    runtimeEnv('VITE_TEMPLATES_BANNER_STATE_KEY') ??
    import.meta.env.VITE_TEMPLATES_BANNER_STATE_KEY,
  VITE_ADMIN_MANAGEMENT_BANNER_STATE_KEY:
    runtimeEnv('VITE_ADMIN_MANAGEMENT_BANNER_STATE_KEY') ??
    import.meta.env.VITE_ADMIN_MANAGEMENT_BANNER_STATE_KEY,
  VITE_CHATBOT_KEY:
    runtimeEnv('VITE_CHATBOT_KEY') ?? import.meta.env.VITE_CHATBOT_KEY,
  VITE_CHATBOT_INPUT_LIMIT: Number(
    runtimeEnv('VITE_CHATBOT_INPUT_LIMIT') ??
      import.meta.env.VITE_CHATBOT_INPUT_LIMIT,
  ),
  VITE_DEFAULT_ORGANIZATION_ID: Number(
    runtimeEnv('VITE_DEFAULT_ORGANIZATION_ID') ??
      import.meta.env.VITE_DEFAULT_ORGANIZATION_ID,
  ),
  VITE_SUPPORT_EMAIL:
    runtimeEnv('VITE_SUPPORT_EMAIL') ?? import.meta.env.VITE_SUPPORT_EMAIL,
  VITE_TERMS_AND_CONDITIONS_EXTERNAL_LINK:
    runtimeEnv('VITE_TERMS_AND_CONDITIONS_EXTERNAL_LINK') ??
    import.meta.env.VITE_TERMS_AND_CONDITIONS_EXTERNAL_LINK,
  VITE_PRIVACY_POLICY_EXTERNAL_LINK:
    runtimeEnv('VITE_PRIVACY_POLICY_EXTERNAL_LINK') ??
    import.meta.env.VITE_PRIVACY_POLICY_EXTERNAL_LINK,
  VITE_YEARLY_PLAN_DISCOUNT: Number(
    runtimeEnv('VITE_YEARLY_PLAN_DISCOUNT') ??
      import.meta.env.VITE_YEARLY_PLAN_DISCOUNT,
  ),
  VITE_APPLE_STORE_LINK:
    runtimeEnv('VITE_APPLE_STORE_LINK') ??
    import.meta.env.VITE_APPLE_STORE_LINK,
  VITE_GOOGLE_PLAY_LINK:
    runtimeEnv('VITE_GOOGLE_PLAY_LINK') ??
    import.meta.env.VITE_GOOGLE_PLAY_LINK,
  VITE_GUILLES_HUDELOT_ID:
    runtimeEnv('VITE_GUILLES_HUDELOT_ID') ??
    import.meta.env.VITE_GUILLES_HUDELOT_ID,
}

/**
 * Devtools flag (ENG3002 default: enabled). Set the container env var
 * VITE_ENABLE_DEVTOOLS="false" to disable react-query devtools at runtime.
 */
export const isDevtoolsEnabled = env.VITE_ENABLE_DEVTOOLS !== 'false'
